const CROCKFORD_ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
const BITS_PER_SYMBOL = 5;
const BITS_PER_BYTE = 8;
const SYMBOL_BIT_MASK = 0b11111;
const BYTE_BIT_MASK = 0xff;

const CROCKFORD_DECODE_MAP: ReadonlyMap<string, number> = new Map([
  ...Array.from(CROCKFORD_ALPHABET, (char, index): [string, number] => [
    char,
    index,
  ]),
  ['O', 0],
  ['I', 1],
  ['L', 1],
]);

/** Unpadded Crockford base32; a trailing partial symbol is zero-filled. */
export function crockfordBase32Encode(bytes: Uint8Array): string {
  let output = '';
  let buffer = 0;
  let bitsInBuffer = 0;

  for (const byte of bytes) {
    buffer = (buffer << BITS_PER_BYTE) | byte;
    bitsInBuffer += BITS_PER_BYTE;

    while (bitsInBuffer >= BITS_PER_SYMBOL) {
      bitsInBuffer -= BITS_PER_SYMBOL;
      const index = (buffer >> bitsInBuffer) & SYMBOL_BIT_MASK;
      output += CROCKFORD_ALPHABET[index];
    }
  }

  if (bitsInBuffer > 0) {
    const index =
      (buffer << (BITS_PER_SYMBOL - bitsInBuffer)) & SYMBOL_BIT_MASK;
    output += CROCKFORD_ALPHABET[index];
  }

  return output;
}

/**
 * Case-insensitive, accepts O→0 and I/L→1, drops a trailing partial byte;
 * throws on other characters.
 */
export function crockfordBase32Decode(input: string): Uint8Array {
  const bytes: number[] = [];
  let buffer = 0;
  let bitsInBuffer = 0;

  for (const rawChar of input.toUpperCase()) {
    const value = CROCKFORD_DECODE_MAP.get(rawChar);
    if (value === undefined) {
      throw new Error(`Invalid Crockford base32 character: ${rawChar}`);
    }
    buffer = (buffer << BITS_PER_SYMBOL) | value;
    bitsInBuffer += BITS_PER_SYMBOL;

    if (bitsInBuffer >= BITS_PER_BYTE) {
      bitsInBuffer -= BITS_PER_BYTE;
      bytes.push((buffer >> bitsInBuffer) & BYTE_BIT_MASK);
    }
  }

  return new Uint8Array(bytes);
}
